// Ported from design/project/App39-Deposit.dc.html, then wired to the connected wallet through TON Connect.
import { useState } from "react";
import { ApiError } from "../api.js";
import { Cancelled, cleanAmount, depositFromWallet, useMoney, walletName } from "../money.js";
import { go } from "../router.js";
import { DepositTabs, Line, Notice, SheetBackdrop, SheetTitle } from "./money-parts.js";

const QUICK = ["20", "50", "100", "250"];

/** Where the Connect wallet screen goes once a wallet is connected. */
export const AFTER_CONNECT = "dd:afterConnect";

const PROBLEMS: Record<string, string> = {
  bad_amount: "The smallest deposit is 2 USDT.",
  over_limit: "That would take your balance over 10,000 USDT.",
  deposits_paused: "Deposits are paused for a few minutes. Your balance is safe.",
  not_ready: "Deposits aren't open yet.",
  network: "No connection. Check your internet and try again.",
};

export function App39_Deposit() {
  const { view } = useMoney();
  const [amount, setAmount] = useState("50");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const left = view ? Number(view.deposit.left) : null;
  const deposit = async () => {
    if (!view?.wallet) {
      sessionStorage.setItem(AFTER_CONNECT, "App39");
      go("App04");
      return;
    }
    const v = cleanAmount(amount);
    if (!v) return setProblem(PROBLEMS.bad_amount!);
    setBusy(true);
    setProblem(null);
    // Taken before signing: the deposit can land while the player is still on their way back from the wallet app.
    const started = Date.now();
    try {
      await depositFromWallet(v, view.wallet.address);
      sessionStorage.setItem("dd:pendingDeposit", JSON.stringify({ amount: v, at: started, from: view.wallet.appName }));
      go("App55", true);
    } catch (e) {
      if (!(e instanceof Cancelled)) setProblem(PROBLEMS[e instanceof ApiError ? e.code : ""] ?? "Your wallet didn't send it. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="screen">
  <SheetBackdrop />
  <div className="sheet g squeeze" style={{ background: "rgba(18,22,29,0.96)", borderBottom: "0" }}>
    <SheetTitle>Deposit USDT</SheetTitle>
    <DepositTabs active="wallet" />
    <p className="muted tallOnly" style={{ margin: "6px 0 0", fontSize: "13px", lineHeight: "19px" }}>Sign once in your wallet. After that, every battle runs from your balance with no signing and no gas.</p>

    <label style={{ marginTop: "16px", padding: "18px", borderRadius: "20px", background: "linear-gradient(160deg, rgba(204,255,0,0.12), rgba(22,27,34,0.9) 60%)", border: "1px solid rgba(204,255,0,0.4)", display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
      <span className="lbl">YOU DEPOSIT</span>
      <span className="mono" style={{ fontWeight: "800", fontSize: "44px", lineHeight: "50px", display: "flex", alignItems: "baseline", gap: "8px" }}>
        <input aria-label="Amount in USDT" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.slice(0, 10))}
          style={{ width: `${Math.max(2, amount.length) + 0.5}ch`, maxWidth: "6.5ch", background: "transparent", border: "0", outline: "none", color: "#FFFFFF", font: "inherit", textAlign: "right", padding: "0" }} />
        <span style={{ fontSize: "18px" }} className="muted">USDT</span>
      </span>
      <span className="muted" style={{ fontSize: "12px" }}>{view?.wallet ? `From ${walletName(view.wallet.appName)}` : "From your TON wallet"}</span>
    </label>

    <div style={{ marginTop: "12px", display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "8px" }}>
      {QUICK.map((q) => (
        <button key={q} onClick={() => setAmount(q)} aria-pressed={amount === q} className={amount === q ? "mono" : "g mono"}
          style={amount === q
            ? { height: "42px", borderRadius: "12px", border: "0", background: "#CCFF00", color: "#0B0E14", fontWeight: "700", fontSize: "14px", cursor: "pointer" }
            : { height: "42px", borderRadius: "12px", color: "#FFFFFF", fontWeight: "700", fontSize: "14px", cursor: "pointer" }}>{q}</button>
      ))}
    </div>

    <div className="g" style={{ marginTop: "14px", borderRadius: "18px", padding: "4px 0" }}>
      <Line first label="Minimum deposit"><span className="mono">{view?.deposit.min ?? "2.00"} USDT</span></Line>
      <Line label="Network fee"><span className="mono">under 0.01 TON</span></Line>
      <Line label="Arrives in balance">about 10 seconds</Line>
      <Line label="Balance limit"><span className="mono">10,000</span> · <span className="mono">{left === null ? "..." : left.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> left</Line>
    </div>

    {problem && <Notice>{problem}</Notice>}
    <button onClick={() => void deposit()} disabled={busy || view?.deposit.paused} className="btnP" style={{ marginTop: "16px", border: "0", cursor: "pointer", opacity: busy ? 0.6 : 1 }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0B0E14" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="3"></rect><path d="M16 13h2"></path><path d="M3 10h18"></path></svg>
      {busy ? "CHECK YOUR WALLET" : !view?.wallet ? "CONNECT WALLET" : `DEPOSIT ${cleanAmount(amount) ?? ""} USDT`}</button>
    <span className="muted" style={{ marginTop: "10px", textAlign: "center", fontSize: "12px", lineHeight: "17px" }}>No TON for the fee? <a href="#/App74" onClick={(e) => { e.preventDefault(); go("App74", true); }} style={{ color: "#FFFFFF" }}>Send from @wallet or an exchange</a></span>
  </div>
</div>
    </>
  );
}
